<template>
  <div class="singlecard">
    <figure
      v-if="app.image"
      :class="{ 'opacity-3': isDisabled }"
      class="singlecard__figure overflow-hidden h-50 m-0 p-8"
      @click.stop.prevent="goToApp"
    >
      <img
        width="384"
        height="144"
        loading="lazy"
        :alt="app.name"
        :src="app.image"
        class="w-100 h-100 object-contain"
      >
    </figure>

    <div
      v-else
      data-grid
      :class="{ 'opacity-3': isDisabled }"
      class="singlecard__nofigure grid--center grid--va-center h-50 cursor-pointer"
      @click.stop.prevent="goToApp"
    >
      <h3
        data-grid="center-center"
        class="singlecard__letter m-0 h-100 w-auto"
      >
        {{ app.name.charAt(0).toUpperCase() }}
      </h3>
    </div>

    <div
      data-grid="column"
      class="singlecard__content grid--center h-50 p-relative"
    >
      <h3
        :class="{ 'opacity-3': isDisabled }"
        class="singlecard__name overflow-hidden w-100"
      >
        {{ app.name }}
      </h3>

      <a
        v-if="app.url"
        :href="app.url"
        class="singlecard__url overflow-hidden"
        :class="{ 'opacity-3': isDisabled }"
      >
        {{ app.url }}
      </a>

      <button
        data-grid="column"
        class="singlecard__actions grid--between grid--va-center p-absolute btn"
        @click="toggleTooltipVisibility"
      >
        <span class="singlecard__action-dot d-block" />
        <span class="singlecard__action-dot d-block" />
        <span class="singlecard__action-dot d-block" />
      </button>

      <AsyncTooltip
        :app="app"
        :showing="shouldTooltipBeVisible"
        @edit-clicked="goToApp"
        @toggle-view="toggleTooltipVisibility"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, defineAsyncComponent } from 'vue'

import { mapActions } from 'vuex'

const AsyncTooltip = defineAsyncComponent(() => import('../../../global/Tooltip'))

export default defineComponent({
  name: 'Card',

  components: {
    AsyncTooltip
  },

  props: {
    app: {
      type: Object,
      required: true
    },

    dashType: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      shouldTooltipBeVisible: false
    }
  },

  computed: {
    url () {
      const { app, dashType } = this

      return {
        'dash-pwa': `/checklist/${app.id}`,
        'dash-checkout': `/checklist/${app.id}`
      }[dashType] || ''
    },

    tooltipOptions () {
      return [
        {
          visible: this.app.status,
          label: this.$t('edit')
        }, {
          visible: true,
          label: this.$t('disable')
        }
      ]
    },

    isDisabled () {
      return this.app?.status === 0
    }
  },

  methods: {
    ...mapActions({
      setApp: 'apps/setApp',
      searchApp: 'apps/searchApp'
    }),

    toggleTooltipVisibility () {
      this.shouldTooltipBeVisible = !this.shouldTooltipBeVisible
    },

    async goToApp () {
      this.setApp(this.app)

      await this.$router.push({
        path: this.url
      })

      this.searchApp()
    }
  }
})
</script>

<style lang="scss" scoped>
  .singlecard {
    border-radius: 10px;
    aspect-ratio: 1 / .75;
    width: min(100%, 384px);

    background-color: rgb(var(--white));
    box-shadow: 0 4px 20px rgba(32, 39, 44, 0.1);

    &, & * {
      transition: opacity .4s ease-in-out;
    }

    &__content,
    &__nofigure {
      padding: var(--gap);
    }

    &__figure,
    &__nofigure {
      border-radius: 10px 10px 0 0;
      background-color: rgb(var(--gray6));
    }

    &__letter {
      aspect-ratio: 1;
      border-radius: 50%;
      background-color: rgb(var(--gray8));

      color: var(--primary);
      font: 400 6rem "Nunito", sans-serif;
    }

    &__name {
      max-width: calc(100% - 21px);
      color: rgb(var(--black));
      font: 700 2.5rem / 1.36 "Nunito", sans-serif;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
    }

    &__url {
      color: rgb(var(--gray3));
      font: 400 1.6rem / 1.375 "Nunito", sans-serif;

      max-width: 100%;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
    }

    &__actions {
      top: var(--gap);
      right: var(--gap);

      padding: 0 2px;
      background-color: rgb(var(--white));

      width: 21px;
      height: 21px;
    }

    &__action-dot {
      width: 3px;
      height: 3px;
      margin-block: 2px;
      background-color: rgb(var(--gray3));
    }
  }
</style>
